"use client";

import { useState } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/context/language-context";
import { BlurReveal } from "@/components/blur-reveal";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import GithubGraph from "@/components/github-graph";
import { SkillIcon } from "@/components/skill-icon";
import { useMediaQuery } from "@/hooks/use-media-query";

type StackItem = {
  name: string;
  icon: string;
  hint?: string;
};

type StackCategory = {
  title: string;
  items: StackItem[];
};

function skillId(category: string, name: string) {
  return `${category}::${name}`;
}

function SkillItem({
  item,
  category,
  hasHover,
  isOpen,
  onOpenChange,
}: {
  item: StackItem;
  category: string;
  hasHover: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [hoverOpen, setHoverOpen] = useState(false);

  return (
    <HoverCard
      open={hasHover ? hoverOpen : isOpen}
      onOpenChange={hasHover ? setHoverOpen : onOpenChange}
      openDelay={hasHover ? 80 : 0}
      closeDelay={hasHover ? 80 : 150}
    >
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="group focus-ring flex shrink-0 cursor-pointer items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors hover:bg-secondary/30"
          onClick={() => {
            if (!hasHover) onOpenChange(!isOpen);
          }}
        >
          <div className="transition-all duration-500 ease-out group-hover:scale-110">
            <SkillIcon icon={item.icon} name={item.name} size={20} />
          </div>
          <span className="text-sm tracking-wide text-muted-foreground transition-colors duration-500 ease-out group-hover:text-foreground">
            {item.name}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side={hasHover ? "top" : "bottom"}
        align="center"
        className="relative flex w-72 flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border border-border/50 bg-background p-4 pt-12 shadow-2xl"
      >
        {!hasHover && (
          <button
            type="button"
            aria-label="Close skill details"
            onClick={() => onOpenChange(false)}
            className="focus-ring absolute top-3 right-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-background text-foreground ring-2 ring-black ring-offset-2 ring-offset-background transition-colors hover:bg-foreground hover:text-background dark:ring-white"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="absolute top-0 right-0 left-0 h-px bg-linear-to-r from-transparent via-primary/50 to-transparent" />
        <div className="pointer-events-none absolute inset-0 bg-linear-to-tr from-foreground/5 to-transparent" />

        <div className="relative rounded-xl bg-secondary/50 p-3 shadow-inner ring-1 ring-border/50">
          <SkillIcon icon={item.icon} name={item.name} size={36} />
        </div>
        <div className="z-10 flex flex-col items-center justify-center gap-1.5 text-center">
          <span className="text-sm font-bold tracking-[0.15em] text-foreground uppercase">
            {item.name}
          </span>
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
            {category}
          </span>
          {item.hint && (
            <p className="mt-1 text-sm leading-relaxed tracking-normal text-pretty text-muted-foreground normal-case">
              {item.hint}
            </p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function Stack() {
  const { content } = useLanguage();
  const hasHover = useMediaQuery("(hover: hover)");
  const [openSkillId, setOpenSkillId] = useState<string | null>(null);

  return (
    <section
      id="stack"
      className="relative w-full overflow-hidden bg-background py-16 text-foreground md:py-24 lg:py-32 xl:py-40 2xl:py-36"
    >
      {!hasHover && openSkillId && (
        <button
          type="button"
          aria-label="Close skill details"
          className="fixed inset-0 z-40 bg-background/50"
          onClick={() => setOpenSkillId(null)}
        />
      )}

      <div className="container mx-auto flex h-full flex-col px-container">
        <div className="mb-16 flex flex-col gap-4">
          <BlurReveal>
            <h2 className="title">{content.stack.title}</h2>
          </BlurReveal>
        </div>

        <div className="flex flex-col gap-8 md:gap-10">
          {content.stack.items.map((category: StackCategory, catIndex: number) => (
            <div key={category.title}>
              <BlurReveal>
                <div className="mb-4 flex items-center gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-muted-foreground/40">
                    0{catIndex + 1}
                  </span>
                  <h3 className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase">
                    {category.title}
                  </h3>
                </div>
              </BlurReveal>

              <div className="flex flex-wrap items-center gap-6">
                {category.items.map((item: StackItem) => {
                  const id = skillId(category.title, item.name);

                  return (
                    <BlurReveal key={item.name}>
                      <SkillItem
                        item={item}
                        category={category.title}
                        hasHover={hasHover}
                        isOpen={openSkillId === id}
                        onOpenChange={(open) => setOpenSkillId(open ? id : null)}
                      />
                    </BlurReveal>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <GithubGraph />
      </div>
    </section>
  );
}
